import { useState, useEffect, useCallback } from 'react';
import { Stack, Center, Loader, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import type { CohortWithDetails } from '../../../../types';
import {
  cohortCoachesApi,
  type CoachScheduleGridResponse,
} from '../../../../services/api/cohortCoaches';
import { CoachScheduleLegend } from './CoachScheduleLegend';
import { CoachScheduleGrid } from './CoachScheduleGrid';
import { CoachesZeroState } from './CoachesZeroState';

interface CoachScheduleViewProps {
  cohort: CohortWithDetails;
  onCoachClick?: (coachUserId: string) => void;
}

export function CoachScheduleView({
  cohort,
  onCoachClick,
}: CoachScheduleViewProps) {
  const [gridData, setGridData] = useState<CoachScheduleGridResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchGrid = useCallback(async () => {
    try {
      setLoading(true);
      const data = await cohortCoachesApi.getScheduleGrid(cohort.id);
      setGridData(data);
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to load schedule grid',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  }, [cohort.id]);

  useEffect(() => {
    fetchGrid();
  }, [fetchGrid]);

  if (loading && !gridData) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  }

  // No coaches at all
  if (gridData && gridData.summary.total_coaches === 0) {
    return <CoachesZeroState onAddCoaches={() => {}} />;
  }

  // No sessions
  if (gridData && gridData.summary.total_sessions === 0) {
    return (
      <Center py="xl">
        <Text size="sm" c="dimmed">
          No sessions found for this cohort. Create sessions in the Sessions tab first.
        </Text>
      </Center>
    );
  }

  return (
    <Stack gap="md">
      <CoachScheduleLegend />
      {gridData && (
        <CoachScheduleGrid
          sessions={gridData.sessions}
          coaches={gridData.coaches}
          timeSlots={gridData.time_slots}
          onCoachClick={onCoachClick}
        />
      )}
    </Stack>
  );
}
